import React from 'react';
import loadable from '@loadable/component';
import getPageDataLoadingAPI from './containers/pageDataLoadingAPI';
import PreviewResolverPage from './containers/PreviewResolverPage/PreviewResolverPage';
import { NotFoundPage } from './containers';

// routeConfiguration needs to initialize containers first
// Otherwise, components will import form container eventually and
// at that point css bundling / imports will happen in wrong order.
import { NamedRedirect } from './components';

const pageDataLoadingAPI = getPageDataLoadingAPI();

// Beta mode: when true, restricts UI to community-only experience
const isBetaMode = process.env.REACT_APP_BETA_MODE === 'true';


const AboutPage = loadable(() => import(/* webpackChunkName: "AboutPage" */ './containers/AboutPage/AboutPage'));
const AudiencePage = loadable(() => import(/* webpackChunkName: "AudiencePage" */ './containers/AudiencePage/AudiencePage'));
const CreatorProfilePage = loadable(() => import(/* webpackChunkName: "CreatorProfilePage" */ './containers/CreatorProfilePage/CreatorProfilePage'));
const SignupPage = loadable(() => import(/* webpackChunkName: "SignupPage" */ './containers/SignupPage/SignupPage'));
const LoginPage = loadable(() => import(/* webpackChunkName: "LoginPage" */ './containers/LoginPage/LoginPage'));
const CheckoutPage = loadable(() => import(/* webpackChunkName: "CheckoutPage" */ './containers/CheckoutPage/CheckoutPage'));
const BookingPage = loadable(() => import(/* webpackChunkName: "BookingPage" */ './containers/BookingPage/BookingPage'));
const ContactDetailsPage = loadable(() => import(/* webpackChunkName: "ContactDetailsPage" */ './containers/ContactDetailsPage/ContactDetailsPage'));
const EditPostPage = loadable(() => import(/* webpackChunkName: "EditPostPage" */ './containers/EditPostPage/EditPostPage'));
const EditListingPage = loadable(() => import(/* webpackChunkName: "EditListingPage" */ './containers/EditListingPage/EditListingPage'));
const EditLiveRoomPage = loadable(() => import(/* webpackChunkName: "EditLiveRoomPage" */ './containers/EditLiveRoomPage/EditLiveRoomPage'));
const EmailSettingsPage = loadable(() => import(/* webpackChunkName: "EmailSettingsPage" */ './containers/EmailSettingsPage/EmailSettingsPage'));
const EmailTurnOffPage = loadable(() => import(/* webpackChunkName: "EmailTurnOffPage" */ './containers/EmailTurnOffPage/EmailTurnOffPage'));
const EmailVerificationPage = loadable(() => import(/* webpackChunkName: "EmailVerificationPage" */ './containers/EmailVerificationPage/EmailVerificationPage'));
const LandingPage = loadable(() => import(/* webpackChunkName: "LandingPage" */ './containers/LandingPage/LandingPage'));
const ListingPage = loadable(() => import(/* webpackChunkName: "ListingPage" */ /* webpackPrefetch: true */ './containers/ListingPage/ListingPage'));
const LiveRoomPage = loadable(() => import(/* webpackChunkName: "LiveRoomPage" */ './containers/LiveRoomPage/LiveRoomPage'));
const PasswordChangePage = loadable(() => import(/* webpackChunkName: "PasswordChangePage" */ './containers/PasswordChangePage/PasswordChangePage'));
const AccountNotificationsPage = loadable(() => import(/* webpackChunkName: "AccountNotificationsPage" */ './containers/AccountNotificationsPage/AccountNotificationsPage'));
const PasswordRecoveryPage = loadable(() => import(/* webpackChunkName: "PasswordRecoveryPage" */ './containers/PasswordRecoveryPage/PasswordRecoveryPage'));
const PasswordResetPage = loadable(() => import(/* webpackChunkName: "PasswordResetPage" */ './containers/PasswordResetPage/PasswordResetPage'));
const PaymentMethodsPage = loadable(() => import(/* webpackChunkName: "PaymentMethodsPage" */ './containers/PaymentMethodsPage/PaymentMethodsPage'));
const PrivacyPolicyPage = loadable(() => import(/* webpackChunkName: "PrivacyPolicyPage" */ './containers/PrivacyPolicyPage/PrivacyPolicyPage'));
const CommunityGuidelinesPage = loadable(() => import(/* webpackChunkName: "CommunityGuidelinesPage" */ './containers/CommunityGuidelinesPage/CommunityGuidelinesPage'));
const ProfilePage = loadable(() => import(/* webpackChunkName: "ProfilePage" */ './containers/ProfilePage/ProfilePage'));
const ProfileSettingsPage = loadable(() => import(/* webpackChunkName: "ProfileSettingsPage" */ './containers/ProfileSettingsPage/ProfileSettingsPage'));
const CreatorSettingsProfilePage = loadable(() => import(/* webpackChunkName: "CreatorSettingsProfilePage" */ './containers/CreatorSettingsPage/CreatorSettingsProfilePage'));
const CreatorSettingsAssignmentsPage = loadable(() => import(/* webpackChunkName: "CreatorSettingsAssignmentsPage" */ './containers/CreatorSettingsPage/CreatorSettingsAssignmentsPage'));
const CreatorSettingsBillingPage = loadable(() => import(/* webpackChunkName: "CreatorSettingsBillingPage" */ './containers/CreatorSettingsPage/CreatorSettingsBillingPage'));
const CreatorSettingsNotificationsPage = loadable(() => import(/* webpackChunkName: "CreatorSettingsNotificationsPage" */ './containers/CreatorSettingsPage/CreatorSettingsNotificationsPage'));
const CreatorSettingsConfigurationsPage = loadable(() => import(/* webpackChunkName: "CreatorSettingsConfigurationsPage" */ './containers/CreatorSettingsPage/CreatorSettingsConfigurationsPage'));
const OrgSettingsProfilePage = loadable(() => import(/* webpackChunkName: "OrgSettingsProfilePage" */ './containers/OrgSettingsPage/OrgSettingsProfilePage'));
const OrgSettingsTeamsPage = loadable(() => import(/* webpackChunkName: "OrgSettingsTeamsPage" */ './containers/OrgSettingsPage/OrgSettingsTeamsPage'));
const OrgSettingsAssignmentsPage = loadable(() => import(/* webpackChunkName: "OrgSettingsAssignmentsPage" */ './containers/OrgSettingsPage/OrgSettingsAssignmentsPage'));
const OrgSettingsBillingPage = loadable(() => import(/* webpackChunkName: "OrgSettingsBillingPage" */ './containers/OrgSettingsPage/OrgSettingsBillingPage'));
const OrgSettingsNotificationsPage = loadable(() => import(/* webpackChunkName: "OrgSettingsNotificationsPage" */ './containers/OrgSettingsPage/OrgSettingsNotificationsPage'));
const OrgSettingsConfigurationsPage = loadable(() => import(/* webpackChunkName: "OrgSettingsConfigurationsPage" */ './containers/OrgSettingsPage/OrgSettingsConfigurationsPage'));
const TermsOfServicePage = loadable(() => import(/* webpackChunkName: "TermsOfServicePage" */ './containers/TermsOfServicePage/TermsOfServicePage'));
const QuickRepliesPage = loadable(() => import(/* webpackChunkName: "QuickRepliesPage" */ './containers/QuickRepliesPage/QuickRepliesPage'));
const PaymentHistoryPage = loadable(() => import(/* webpackChunkName: "PaymentHistoryPage" */ './containers/PaymentHistoryPage/PaymentHistoryPage'));
const AccountSettingsPage = loadable(() => import(/* webpackChunkName: "AccountSettingsPage" */ './containers/AccountSettingsPage/AccountSettingsPage'));
const OrganizationProfilePage = loadable(() => import(/* webpackChunkName: "OrganizationProfilePage" */ './containers/OrganizationProfilePage/OrganizationProfilePage'));
const MeetPage = loadable(() => import(/* webpackChunkName: "MeetPage" */ './containers/MeetPage/MeetPage'));
const ManageSubscriptionsPage = loadable(() => import(/* webpackChunkName: "ManageSubscriptionsPage" */ './containers/ManageSubscriptionsPage/ManageSubscriptionsPage'));
const CalendarPage = loadable(() => import(/* webpackChunkName: "CalendarPage" */ './containers/CalendarPage/CalendarPage'));
const CreatorsPage = loadable(() => import(/* webpackChunkName: "CreatorsPage" */ './containers/CreatorsPage/CreatorsPage'));
const HelpPage = loadable(() => import(/* webpackChunkName: "HelpPage" */ './containers/HelpPage/HelpPage'));
const TopicPage = loadable(() => import(/* webpackChunkName: "TopicPage" */ './containers/HelpPage/TopicPage/TopicPage'));
const ArticlePage = loadable(() => import(/* webpackChunkName: "ArticlePage" */ './containers/HelpPage/ArticlePage/ArticlePage'));
const InsightsPage = loadable(() => import(/* webpackChunkName: "InsightsPage" */ './containers/InsightsPage/InsightsPage'));
const MembersPage = loadable(() => import(/* webpackChunkName: "MembersPage" */ './containers/MembersPage/MembersPage'));
const VerifyOrganizationInvitePage = loadable(() => import(/* webpackChunkName: "VerifyOrganizationInvitePage" */ './containers/VerifyOrganizationInvitePage/VerifyOrganizationInvitePage'));
const VerifyListingInvitePage = loadable(() => import(/* webpackChunkName: "VerifyListingInvitePage" */ './containers/VerifyListingInvitePage/VerifyListingInvitePage'));
const ReviewsPage = loadable(() => import(/* webpackChunkName: "ReviewsPage" */ /* webpackPrefetch: true */ './containers/ReviewsPage/ReviewsPage'));
const CommentsPage = loadable(() => import(/* webpackChunkName: "CommentsPage" */ /* webpackPrefetch: true */ './containers/CommentsPage/CommentsPage'));
const RoleSelectionPage = loadable(() => import(/* webpackChunkName: "RoleSelectionPage" */ /* webpackPrefetch: true */ './containers/RoleSelectionPage/RoleSelectionPage'));
const HostsPage = loadable(() => import(/* webpackChunkName: "HostsPage" */ /* webpackPrefetch: true */ './containers/HostsPage/HostsPage'));
const CommunityPage = loadable(() => import(/* webpackChunkName: "CommunityPage" */ /* webpackPrefetch: true */ './containers/CommunityPage/CommunityPage'));
const CommunityProfilePromptPage = loadable(() => import(/* webpackChunkName: "CommunityProfilePromptPage" */ './containers/CommunityProfilePromptPage/CommunityProfilePromptPage'));
const PostPage = loadable(() => import(/* webpackChunkName: "PostPage" */ './containers/PostPage/PostPage'));
const WatchlistPage = loadable(() => import(/* webpackChunkName: "WatchlistPage" */ './containers/WatchlistPage/WatchlistPage'));
const ContactPage = loadable(() => import(/* webpackChunkName: "ContactPage" */ './containers/ContactPage/ContactPage'));
const OrganizationHomePage = loadable(() => import(/* webpackChunkName: "OrganizationHomePage" */ './containers/OrganizationHomePage/OrganizationHomePage'));
const OrgShowsPage = loadable(() => import(/* webpackChunkName: "OrgShowsPage" */ './containers/OrgShowsPage/OrgShowsPage'));
const CreatorHomePage = loadable(() => import(/* webpackChunkName: "CreatorHomePage" */ './containers/CreatorHomePage/CreatorHomePage'));
const CreatorListingsPage = loadable(() => import(/* webpackChunkName: "CreatorListingsPage" */ './containers/CreatorListingsPage/CreatorListingsPage'));
const CreatorCalendarPage = loadable(() => import(/* webpackChunkName: "CreatorCalendarPage" */ './containers/CreatorCalendarPage/CreatorCalendarPage'));
const OrgCalendarPage = loadable(() => import(/* webpackChunkName: "OrgCalendarPage" */ './containers/OrgCalendarPage/OrgCalendarPage'));
const BridgeSignupPage = loadable(() => import(/* webpackChunkName: "BridgeSignupPage" */ './containers/BridgeSignupPage/BridgeSignupPage'));
const DashboardPage = loadable(() => import(/* webpackChunkName: "DashboardPage" */ './containers/DashboardPage/DashboardPage'));
const EditCreatorProfilePage = loadable(() => import(/* webpackChunkName: "EditCreatorProfilePage" */ './containers/EditCreatorProfilePage/EditCreatorProfilePage'));
const UpgradePage = loadable(() => import(/* webpackChunkName: "UpgradePage" */ './containers/UpgradePage/UpgradePage'));
const NewOrganizationPage = loadable(() => import(/* webpackChunkName: "NewOrganizationPage" */ './containers/NewOrganizationPage/NewOrganizationPage'));
const CreatorConversationsPage = loadable(() => import(/* webpackChunkName: "CreatorConversationsPage" */ './containers/CreatorConversationsPage'));
const OrgConversationsPage = loadable(() => import(/* webpackChunkName: "OrgConversationsPage" */ './containers/OrgConversationsPage'));
const AudienceConversationsPage = loadable(() => import(/* webpackChunkName: "AudienceConversationsPage" */ './containers/AudienceConversationsPage'));
const CreatorAudiencePage = loadable(() => import(/* webpackChunkName: "CreatorAudiencePage" */ './containers/CreatorAudiencePage'));
const OrgAudiencePage = loadable(() => import(/* webpackChunkName: "OrgAudiencePage" */ './containers/OrgAudiencePage'));

// Styleguide helps you to review current components and develop new ones
const StyleguidePage = loadable(() => import(/* webpackChunkName: "StyleguidePage" */ './containers/StyleguidePage/StyleguidePage'));

export const ACCOUNT_SETTINGS_PAGES = [
  'ProfileSettingsPage',
  'ContactDetailsPage',
  'PasswordChangePage',
  'AccountNotificationsPage',
  'PaymentMethodsPage',
];

export const DASHBOARD_PAGES = [
  'DashboardPage',
  // Creator dashboard pages
  'CreatorHomePage',
  'EditCreatorProfilePage',
  'CreatorAudiencePage',
  'CreatorManageListingsPage',
  'CreatorInsightsPage',
  'CreatorCalendarPage',
  'CreatorConversationsPage',
  'CreatorNewOrganizationPage',
  'CreatorSettingsProfilePage',
  'CreatorSettingsAssignmentsPage',
  'CreatorSettingsBillingPage',
  'CreatorSettingsBillingOnboardingPage',
  'CreatorSettingsNotificationsPage',
  'CreatorSettingsConfigurationsPage',
  'OrgSettingsProfilePage',
  'OrgSettingsTeamsPage',
  'OrgSettingsAssignmentsPage',
  'OrgSettingsBillingPage',
  'OrgSettingsNotificationsPage',
  'OrgSettingsConfigurationsPage',
  // Organization dashboard pages (with :orgId)
  'OrganizationHomePage',
  'OrgManageShowsPage',
  'OrgCalendarPage',
  'QuickRepliesPage',
  'OrgInsightsPage',
  'OrgAudiencePage',
  'MembersPage',
  'NewOrganizationPage',
  // Note: VerifyOrganizationInvitePage is intentionally NOT here — it's a
  // one-off invite-accept landing for not-yet-team-members, so it should
  // render the audience-facing Topbar (LandingPage variant), not the
  // dashboard one. Same goes for VerifyListingInvitePage.
  'PaymentHistoryPage',
  'UpgradePage',
  'OrgConversationsPage',
];

// https://en.wikipedia.org/wiki/Universally_unique_identifier#Nil_UUID
const draftId = '00000000-0000-0000-0000-000000000000';
const draftSlug = 'draft';

const RedirectToLandingPage = () => <NamedRedirect name="LandingPage" />;

// NOTE: Most server-side endpoints are prefixed with /api. Requests to those
// endpoints are indended to be handled in the server instead of the browser and
// they will not render the application. So remember to avoid routes starting
// with /api and if you encounter clashing routes see server/index.js if there's
// a conflicting route defined there.

// What stays open in Beta mode; every other page redirects to Community
export const BETA_ALLOWED_ROUTES = new Set([
  // Community pages
  'CommunityPage',
  'CommunityProfilePromptPage',
  'PostPage',
  'EditPostPage',
  'NewPostPage',
  'CommunityGuidelinesPage',
  // Auth pages
  'LoginPage',
  'CommunitySignupPage',
  'AudienceSignupPage',
  'SignupPage',
  'ConfirmLoginPage',
  'ConfirmSignupPage',
  'RoleSelectionPage',
  'PasswordRecoveryPage',
  'PasswordResetPage',
  'EmailVerificationPage',
  // From a link in an email: a person can always unsubscribe (CASL)
  'EmailSettingsPage',
  'EmailTurnOffPage',
  // Legal / static
  'TermsOfServicePage',
  'PrivacyPolicyPage',
  'NotFoundPage',
  'PreviewResolverPage',
  // Account (needed for logged-in users)
  'AccountSettingsPage',
  'ContactDetailsPage',
  'PasswordChangePage',
  'AccountNotificationsPage',
  'ProfileSettingsPage',
  'ProfilePage',
  // Bridge signup (audience upgrade)
  'BridgeSignupPage',
  'RoleSelectionPage',
]);

// Our routes are exact by default.
// See behaviour from Routes.js where Route is created.
const routeConfiguration = () => {
  const allRoutes = [
    {
      path: '/',
      name: 'LandingPage',
      component: LandingPage,
      loadData: pageDataLoadingAPI.LandingPage.loadData,
      loadClientData: pageDataLoadingAPI.LandingPage.loadClientData,
    },
    {
      path: '/about',
      name: 'AboutPage',
      component: AboutPage,
    },
    {
      path: '/audience',
      name: 'AudiencePage',
      component: AudiencePage,
    },
    {
      path: '/creator/:id',
      name: 'CreatorProfilePage',
      component: CreatorProfilePage,
      loadData: pageDataLoadingAPI.CreatorProfilePage.loadData,
      loadClientData: pageDataLoadingAPI.CreatorProfilePage.loadClientData,
    },
    {
      path: '/creators',
      name: 'CreatorsPage',
      component: CreatorsPage,
    },
    // ListingPage (ShowPage)
    {
      path: '/page',
      name: 'ListingBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/page/:slug/:id',
      name: 'ListingPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
      loadClientData: pageDataLoadingAPI.ListingPage.loadClientData,
    },
    {
      path: '/page/:slug/:id/:variant',
      name: 'ListingPageVariant',
      auth: true,
      authPage: 'LoginPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
      loadClientData: pageDataLoadingAPI.ListingPage.loadClientData,
    },
    {
      path: '/page/new',
      name: 'NewListingPage',
      auth: true,
      authPage: 'CreatorSignupPage',
      component: ({ location }) => (
        <NamedRedirect
          name="EditListingPage"
          params={{ slug: draftSlug, id: draftId, type: 'new', tab: 'basic' }}
          search={location.search}
        />
      ),
    },
    {
      path: '/page/:slug/:id/:type/:tab',
      name: 'EditListingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },
    {
      path: '/page/:slug/:id/:type/:tab/:returnURLType',
      name: 'EditListingStripeOnboardingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },
    // LiveRoomPage
    {
      path: '/live-room',
      name: 'LiveRoomBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/live-room/:slug/:id',
      name: 'LiveRoomPage',
      component: LiveRoomPage,
      loadData: pageDataLoadingAPI.LiveRoomPage.loadData,
      loadClientData: pageDataLoadingAPI.LiveRoomPage.loadClientData,
    },
    {
      path: '/live-room/:slug/:id/checkout',
      name: 'CheckoutPage',
      auth: true,
      authPage: 'LoginPage',
      component: CheckoutPage,
      setInitialValues: pageDataLoadingAPI.CheckoutPage.setInitialValues,
    },
    {
      path: '/live-room/:slug/:id/booking',
      name:'BookingPage',
      auth: true,
      authPage: 'LoginPage',
      component: BookingPage,
      loadData: pageDataLoadingAPI.BookingPage.loadData,
      setInitialValues: pageDataLoadingAPI.BookingPage.setInitialValues,
    },
    {
      path: '/live-room/:slug/:id/:variant',
      name: 'LiveRoomPageVariant',
      auth: true,
      authPage: 'LoginPage',
      component: LiveRoomPage,
      loadData: pageDataLoadingAPI.LiveRoomPage.loadData,
      loadClientData: pageDataLoadingAPI.LiveRoomPage.loadClientData,
    },
    {
      path: '/live-room/new',
      name: 'NewLiveRoomPage',
      auth: true,
      authPage: 'CreatorSignupPage',
      component: () => (
        <NamedRedirect
          name="EditLiveRoomPage"
          params={{ slug: draftSlug, id: draftId, type: 'new', tab: 'details' }}
        />
      ),
    },
    {
      path: '/live-room/:slug/:id/:type/:tab',
      name: 'EditLiveRoomPage',
      auth: true,
      component: EditLiveRoomPage,
      loadData: pageDataLoadingAPI.EditLiveRoomPage.loadData,
    },
    {
      path: '/live-room/:slug/:id/:type/:tab/:returnURLType',
      name: 'EditLiveRoomStripeOnboardingPage',
      auth: true,
      component: EditLiveRoomPage,
      loadData: pageDataLoadingAPI.EditLiveRoomPage.loadData,
    },
    // ReviewsPage
    {
      path: '/:slug/:id/reviews',
      name: 'ReviewsPage',
      component: ReviewsPage,
      loadData: pageDataLoadingAPI.ReviewsPage.loadData,
      loadClientData: pageDataLoadingAPI.ReviewsPage.loadClientData,
    },
    // CommentsPage
    {
      path: '/:slug/:id/comments',
      name: 'CommentsPage',
      component: CommentsPage,
      loadData: pageDataLoadingAPI.CommentsPage.loadData,
      loadClientData: pageDataLoadingAPI.CommentsPage.loadClientData,
    },
    // HostsPage
    {
      path: '/:slug/:id/hosts',
      name: 'HostsPage',
      component: HostsPage,
      loadData: pageDataLoadingAPI.HostsPage.loadData,
      loadClientData: pageDataLoadingAPI.HostsPage.loadClientData,
    },
    {
      path: '/community',
      name: 'CommunityPage',
      component: CommunityPage,
      loadData: pageDataLoadingAPI.CommunityPage.loadData,
      loadClientData: pageDataLoadingAPI.CommunityPage.loadClientData,
    },
    {
      path: '/join/complete',
      name: 'CommunityProfilePromptPage',
      auth: true,
      authPage: 'LoginPage',
      component: CommunityProfilePromptPage,
    },
    {
      path: '/post/:slug/:id',
      name: 'PostPage',
      component: PostPage,
      loadData: pageDataLoadingAPI.PostPage.loadData,
      loadClientData: pageDataLoadingAPI.PostPage.loadClientData,
    },
    {
      path: '/post/:slug/:id/:type/:tab',
      name: 'EditPostPage',
      auth: true,
      authPage: 'LoginPage',
      loadData: pageDataLoadingAPI.EditPostPage.loadData,
      component: EditPostPage,
    },
    {
      path: '/post/new',
      name: 'NewPostPage',
      auth: true,
      authPage: isBetaMode ? 'AudienceSignupPage' : 'CreatorSignupPage',
      component: () => (
        <NamedRedirect
          name="EditPostPage"
          params={{ slug: draftSlug, id: draftId, type: 'new', tab: 'text' }}
        />
      ),
    },
    // Canonical path should be after the `/page/new` path since they
    // conflict and `new` is not a valid listing UUID.
    {
      path: '/page/:id',
      name: 'ListingPageCanonical',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
      loadClientData: pageDataLoadingAPI.ListingPage.loadClientData,
    },
    {
      path: '/user',
      name: 'ProfileBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/user/:id',
      name: 'ProfilePage',
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage.loadData,
      loadClientData: pageDataLoadingAPI.ProfilePage.loadClientData,
    },
    {
      path: '/account/profile-settings',
      name: 'ProfileSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ProfileSettingsPage,
    },
    // Creator Settings — split per-tab so URLs reflect the active tab
    {
      path: '/dashboard/creator/settings',
      name: 'CreatorSettingsBasePage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="CreatorSettingsProfilePage" />,
    },
    {
      path: '/dashboard/creator/settings/profile',
      name: 'CreatorSettingsProfilePage',
      auth: true,
      authPage: 'LoginPage',
      component: CreatorSettingsProfilePage,
    },
    {
      path: '/dashboard/creator/settings/assignments',
      name: 'CreatorSettingsAssignmentsPage',
      auth: true,
      authPage: 'LoginPage',
      component: CreatorSettingsAssignmentsPage,
      loadData: pageDataLoadingAPI.CreatorSettingsAssignmentsPage.loadData,
    },
    {
      path: '/dashboard/creator/settings/billing',
      name: 'CreatorSettingsBillingPage',
      auth: true,
      authPage: 'LoginPage',
      component: CreatorSettingsBillingPage,
      loadData: pageDataLoadingAPI.CreatorSettingsBillingPage.loadData,
    },
    {
      // Return URL after Stripe Connect onboarding. Same component +
      // loadData as the base page; the returnURLType param ('success'
      // or 'failure') drives any post-Stripe handling inside.
      path: '/dashboard/creator/settings/billing/:returnURLType(success|failure)',
      name: 'CreatorSettingsBillingOnboardingPage',
      auth: true,
      authPage: 'LoginPage',
      component: CreatorSettingsBillingPage,
      loadData: pageDataLoadingAPI.CreatorSettingsBillingPage.loadData,
    },
    {
      path: '/dashboard/creator/settings/notifications',
      name: 'CreatorSettingsNotificationsPage',
      auth: true,
      authPage: 'LoginPage',
      component: CreatorSettingsNotificationsPage,
      loadData: pageDataLoadingAPI.CreatorSettingsNotificationsPage.loadData,
    },
    {
      path: '/dashboard/creator/settings/configurations',
      name: 'CreatorSettingsConfigurationsPage',
      auth: true,
      authPage: 'LoginPage',
      component: CreatorSettingsConfigurationsPage,
      loadData: pageDataLoadingAPI.CreatorSettingsConfigurationsPage.loadData,
    },
    // Audience pages (ex. audience dashboard pages)
    {
      path: '/subscriptions',
      name: 'ManageSubscriptionsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ManageSubscriptionsPage,
      loadData: pageDataLoadingAPI.ManageSubscriptionsPage.loadData,
    },
    {
      path: '/watchlist',
      name: 'WatchlistPage',
      auth: true,
      authPage: 'LoginPage',
      component: WatchlistPage,
      loadData: pageDataLoadingAPI.WatchlistPage.loadData,
    },
    {
      path: '/contact',
      name: 'ContactPage',
      component: ContactPage,
    },
    {
      path: '/calendar',
      name: 'CalendarPage',
      auth: true,
      authPage: 'LoginPage',
      component: CalendarPage,
      loadData: pageDataLoadingAPI.CalendarPage.loadData,
    },
    {
      path: '/conversations/:channelId?',
      name: 'AudienceConversationsPage',
      auth: true,
      authPage: 'LoginPage',
      component: AudienceConversationsPage,
      loadData: pageDataLoadingAPI.AudienceConversationsPage.loadData,
      loadDataIgnoresParams: ['channelId'],
    },
    // =====================================================
    // DASHBOARD ROUTES
    // =====================================================

    // Dashboard redirect - routes to appropriate home page based on last visited context
    {
      path: '/dashboard',
      name: 'DashboardPage',
      auth: true,
      authPage: 'LoginPage',
      component: DashboardPage,
      loadData: pageDataLoadingAPI.DashboardPage?.loadData,
    },

    // =====================================================
    // CREATOR DASHBOARD ROUTES (no :orgId - one per user)
    // =====================================================
    {
      path: '/dashboard/creator/home',
      name: 'CreatorHomePage',
      auth: true,
      authPage: 'LoginPage',
      component: CreatorHomePage,
      loadData: pageDataLoadingAPI.CreatorHomePage.loadData,
    },
    {
      path: '/dashboard/creator/profile',
      name: 'EditCreatorProfilePage',
      auth: true,
      authPage: 'LoginPage',
      component: EditCreatorProfilePage,
    },
    {
      path: '/dashboard/creator/audience/:tab?',
      name: 'CreatorAudiencePage',
      auth: true,
      authPage: 'LoginPage',
      component: CreatorAudiencePage,
      loadData: pageDataLoadingAPI.CreatorAudiencePage.loadData,
    },
    {
      path: '/dashboard/creator/listings',
      name: 'CreatorManageListingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: CreatorListingsPage,
      loadData: pageDataLoadingAPI.CreatorListingsPage.loadData,
    },
    {
      path: '/dashboard/creator/insights/:tab?',
      name: 'CreatorInsightsPage',
      auth: true,
      authPage: 'LoginPage',
      component: InsightsPage,
      loadData: pageDataLoadingAPI.InsightsPage.loadData,
    },
    {
      path: '/dashboard/creator/calendar',
      name: 'CreatorCalendarPage',
      auth: true,
      authPage: 'LoginPage',
      component: CreatorCalendarPage,
      loadData: pageDataLoadingAPI.CreatorCalendarPage.loadData,
    },
    {
      path: '/dashboard/creator/conversations/:channelId?',
      name: 'CreatorConversationsPage',
      auth: true,
      authPage: 'LoginPage',
      component: CreatorConversationsPage,
      loadData: pageDataLoadingAPI.CreatorConversationsPage?.loadData,
      loadDataIgnoresParams: ['channelId'],
    },

    {
      path: '/dashboard/creator/new-organization',
      name: 'CreatorNewOrganizationPage',
      auth: true,
      component: NewOrganizationPage,
    },

    // =====================================================
    // ORGANIZATION DASHBOARD ROUTES (with :orgId param)
    // =====================================================
    {
      path: '/dashboard/org/:orgId/home',
      name: 'OrganizationHomePage',
      auth: true,
      authPage: 'LoginPage',
      component: OrganizationHomePage,
      loadData: pageDataLoadingAPI.OrganizationHomePage.loadData,
    },
    {
      path: '/dashboard/org/:orgId/listing',
      name: 'OrgManageShowsPage',
      auth: true,
      authPage: 'LoginPage',
      component: OrgShowsPage,
      loadData: pageDataLoadingAPI.OrgManageShowsPage.loadData,
    },
    {
      path: '/dashboard/org/:orgId/calendar',
      name: 'OrgCalendarPage',
      auth: true,
      authPage: 'LoginPage',
      component: OrgCalendarPage,
      loadData: pageDataLoadingAPI.OrgCalendarPage.loadData,
    },
    {
      path: '/dashboard/org/:orgId/conversations/:channelId?',
      name: 'OrgConversationsPage',
      auth: true,
      authPage: 'LoginPage',
      component: OrgConversationsPage,
      loadData: pageDataLoadingAPI.OrgConversationsPage?.loadData,
      loadDataIgnoresParams: ['channelId'],
    },
    {
      path: '/dashboard/org/:orgId/quick-replies',
      name: 'QuickRepliesPage',
      auth: true,
      authPage: 'LoginPage',
      component: QuickRepliesPage,
      loadData: pageDataLoadingAPI.QuickRepliesPage.loadData,
    },
    {
      path: '/dashboard/org/:orgId/insights/:tab?',
      name: 'OrgInsightsPage',
      auth: true,
      authPage: 'LoginPage',
      component: InsightsPage,
      loadData: pageDataLoadingAPI.InsightsPage.loadData,
    },
    {
      path: '/dashboard/org/:orgId/audience/:tab?',
      name: 'OrgAudiencePage',
      auth: true,
      authPage: 'LoginPage',
      component: OrgAudiencePage,
      loadData: pageDataLoadingAPI.OrgAudiencePage.loadData,
    },
    {
      path: '/dashboard/org/:orgId/team',
      name: 'MembersPage',
      auth: true,
      authPage: 'LoginPage',
      component: MembersPage,
      loadData: pageDataLoadingAPI.MembersPage.loadData,
    },
    // Org Settings — split per-tab to mirror Creator Settings
    {
      path: '/dashboard/org/:orgId/settings/profile',
      name: 'OrgSettingsProfilePage',
      auth: true,
      authPage: 'LoginPage',
      component: OrgSettingsProfilePage,
    },
    {
      path: '/dashboard/org/:orgId/settings/teams',
      name: 'OrgSettingsTeamsPage',
      auth: true,
      authPage: 'LoginPage',
      component: OrgSettingsTeamsPage,
      loadData: pageDataLoadingAPI.OrgSettingsTeamsPage.loadData,
    },
    {
      path: '/dashboard/org/:orgId/settings/assignments',
      name: 'OrgSettingsAssignmentsPage',
      auth: true,
      authPage: 'LoginPage',
      component: OrgSettingsAssignmentsPage,
      loadData: pageDataLoadingAPI.OrgSettingsAssignmentsPage.loadData,
    },
    {
      path: '/dashboard/org/:orgId/settings/billing',
      name: 'OrgSettingsBillingPage',
      auth: true,
      authPage: 'LoginPage',
      component: OrgSettingsBillingPage,
      loadData: pageDataLoadingAPI.OrgSettingsBillingPage.loadData,
    },
    {
      path: '/dashboard/org/:orgId/settings/notifications',
      name: 'OrgSettingsNotificationsPage',
      auth: true,
      authPage: 'LoginPage',
      component: OrgSettingsNotificationsPage,
      loadData: pageDataLoadingAPI.OrgSettingsNotificationsPage.loadData,
    },
    {
      path: '/dashboard/org/:orgId/settings/configurations',
      name: 'OrgSettingsConfigurationsPage',
      auth: true,
      authPage: 'LoginPage',
      component: OrgSettingsConfigurationsPage,
      loadData: pageDataLoadingAPI.OrgSettingsConfigurationsPage.loadData,
    },
    {
      path: '/dashboard/org/:orgId/settings/new',
      name: 'NewOrganizationPage',
      auth: true,
      component: NewOrganizationPage,
    },
    {
      path: '/dashboard/org/:orgId/settings/transaction-history',
      name: 'PaymentHistoryPage',
      auth: true,
      authPage: 'LoginPage',
      component: PaymentHistoryPage,
      loadData: pageDataLoadingAPI.PaymentHistoryPage.loadData,
    },
    {
      path: '/dashboard/org/:orgId/upgrade',
      name: 'UpgradePage',
      auth: true,
      authPage: 'LoginPage',
      component: UpgradePage,
      loadData: pageDataLoadingAPI.UpgradePage.loadData,
    },

    // =====================================================
    // ORGANIZATION INVITE ROUTES
    // =====================================================
    {
      // Canonical accept URL emailed to invitees. NO `auth: true` here — the
      // page detects unauthenticated visitors itself and redirects them to
      // /signup with a `from` state carrying this URL (and its invite token),
      // so they land back here after auth and the token is consumed.
      path: '/verify-organization-invite',
      name: 'VerifyOrganizationInvitePage',
      component: VerifyOrganizationInvitePage,
      loadData: pageDataLoadingAPI.VerifyOrganizationInvitePage.loadData,
      loadClientData: pageDataLoadingAPI.VerifyOrganizationInvitePage.loadClientData,
    },
    {
      path: '/verify-invite',
      name: 'VerifyListingInvitePage',
      auth: true,
      authPage: 'LoginPage',
      component: VerifyListingInvitePage,
      loadData: pageDataLoadingAPI.VerifyListingInvitePage.loadData,
    },
    {
      path: '/signup/verify-invite',
      name: 'VerifyListingInvitePageWithSignup',
      auth: true,
      authPage: 'CreatorSignupPage',
      component: VerifyListingInvitePage,
      loadData: pageDataLoadingAPI.VerifyListingInvitePage.loadData,
    },

    // =====================================================
    // LEGACY REDIRECTS (backward compatibility)
    // =====================================================
    {
      path: '/dashboard/creator-home',
      name: 'LegacyCreatorHomePage',
      auth: true,
      component: () => <NamedRedirect name="CreatorHomePage" />,
    },
    {
      path: '/dashboard/creator-profile',
      name: 'LegacyEditCreatorProfilePage',
      auth: true,
      component: () => <NamedRedirect name="EditCreatorProfilePage" />,
    },
    {
      path: '/dashboard/organization-home',
      name: 'LegacyOrganizationHomePage',
      auth: true,
      component: DashboardPage, // Will redirect to appropriate org based on context
    },
    {
      path: '/dashboard/listings',
      name: 'LegacyManageListingsPage',
      auth: true,
      component: DashboardPage,
    },
    {
      path: '/dashboard/analytics',
      name: 'LegacyAnalyticsPage',
      auth: true,
      component: DashboardPage,
    },
    {
      path: '/dashboard/members',
      name: 'LegacyMembersPage',
      auth: true,
      component: DashboardPage,
    },
    {
      path: '/o/:id',
      name: 'OrganizationProfilePage',
      component: OrganizationProfilePage,
      loadData: pageDataLoadingAPI.OrganizationProfilePage.loadData,
      loadClientData: pageDataLoadingAPI.OrganizationProfilePage.loadClientData,
    },
    {
      path: '/meet/:roomName',
      name: 'MeetPage',
      auth: true,
      authPage: 'LoginPage',
      component: MeetPage,
      loadData: pageDataLoadingAPI.MeetPage.loadData,
    },
    // Note: authenticating with IdP (e.g. Facebook) expects that /login path exists
    // so that in the error case users can be redirected back to the LoginPage
    // In case you change this, remember to update the route in server/api/auth/loginWithIdp.js
    {
      path: '/role-selection',
      name: 'RoleSelectionPage',
      component: RoleSelectionPage,
    },
    {
      path: '/upgrade/:role',
      name: 'BridgeSignupPage',
      auth: true,
      authPage: 'LoginPage',
      component: BridgeSignupPage,
    },
    {
      path: '/login',
      name: 'LoginPage',
      component: LoginPage,
      extraProps: { tab: 'login'},
    },
    {
      path: '/signup/:role/:step?',
      name: 'SignupPage',
      component: SignupPage,
      extraProps: { tab: 'signup' },
    },
    {
      path: '/signup/creator',
      name: 'CreatorSignupPage',
      component: SignupPage,
      extraProps: { tab: 'signup' },
    },
    {
      path: '/signup/audience',
      name: 'AudienceSignupPage',
      component: SignupPage,
      extraProps: { tab: 'signup' },
    },
    {
      path: '/join',
      name: 'CommunitySignupPage',
      component: SignupPage,
      extraProps: { tab: 'signup' },
    },
    {
      path: '/confirm-login',
      name: 'ConfirmLoginPage',
      component: LoginPage,
      extraProps: { tab: 'confirm' },
    },
    {
      path: '/confirm-signup/:role',
      name: 'ConfirmSignupPage',
      component: SignupPage,
      extraProps: { tab: 'confirm' },
    },
    {
      path: '/recover-password',
      name: 'PasswordRecoveryPage',
      component: PasswordRecoveryPage,
    },
    {
      path: '/account',
      name: 'AccountSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: AccountSettingsPage,
    },
    {
      path: '/account/contact-details',
      name: 'ContactDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ContactDetailsPage,
      loadData: pageDataLoadingAPI.ContactDetailsPage.loadData,
    },
    {
      path: '/account/change-password',
      name: 'PasswordChangePage',
      auth: true,
      authPage: 'LoginPage',
      component: PasswordChangePage,
    },
    {
      path: '/account/notifications',
      name: 'AccountNotificationsPage',
      auth: true,
      authPage: 'LoginPage',
      component: AccountNotificationsPage,
      loadData: pageDataLoadingAPI.AccountNotificationsPage.loadData,
    },
    {
      path: '/account/payment-methods',
      name: 'PaymentMethodsPage',
      auth: true,
      authPage: 'LoginPage',
      component: PaymentMethodsPage,
      loadData: pageDataLoadingAPI.PaymentMethodsPage.loadData,
    },
    {
      path: '/terms-of-service',
      name: 'TermsOfServicePage',
      component: TermsOfServicePage,
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicyPage',
      component: PrivacyPolicyPage,
    },
    {
      path: '/community-guidelines',
      name: 'CommunityGuidelinesPage',
      component: CommunityGuidelinesPage,
    },
    {
      path: '/styleguide',
      name: 'Styleguide',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/g/:group',
      name: 'StyleguideGroup',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component',
      name: 'StyleguideComponent',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example',
      name: 'StyleguideComponentExample',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example/raw',
      name: 'StyleguideComponentExampleRaw',
      component: StyleguidePage,
      extraProps: { raw: true },
    },
    {
      path: '/notfound',
      name: 'NotFoundPage',
      component: props => <NotFoundPage {...props} />,
    },
    {
      path: '/help',
      name: 'HelpPage',
      component: HelpPage,
    },
    {
      path: '/help/:topicId',
      name: 'TopicPage',
      component: TopicPage,
    },
    {
      path: '/help/:topicId/:articleId',
      name: 'ArticlePage',
      component: ArticlePage,
    },
    // Opened from a sealed link, so the data waits for the browser (server/dataLoader.js)
    {
      path: '/email/turn-off',
      name: 'EmailTurnOffPage',
      component: EmailTurnOffPage,
      loadData: pageDataLoadingAPI.EmailTurnOffPage.loadData,
      loadInBrowser: true,
    },
    {
      path: '/email/settings',
      name: 'EmailSettingsPage',
      component: EmailSettingsPage,
      loadData: pageDataLoadingAPI.EmailSettingsPage.loadData,
      loadInBrowser: true,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /reset-password endpoint
    {
      path: '/reset-password',
      name: 'PasswordResetPage',
      component: PasswordResetPage ,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /verify-email endpoint
    {
      path: '/verify-email',
      name: 'EmailVerificationPage',
      auth: true,
      authPage: 'LoginPage',
      component: EmailVerificationPage,
      loadData: pageDataLoadingAPI.EmailVerificationPage.loadData,
    },
     // Do not change this path!
    //
    // The API expects that the application implements /preview endpoint
    {
      path: '/preview',
      name: 'PreviewResolverPage',
      component: PreviewResolverPage ,
    },
  ];

  if (!isBetaMode) {
    return allRoutes;
  }

  // In beta mode, only community-related routes are accessible.
  // Everything else redirects to CommunityPage.

  return allRoutes.map(route => {
    if (route.name === 'LandingPage') {
      // Redirect "/" to community page
      return { ...route, component: () => <NamedRedirect name="CommunityPage" /> };
    }
    if (BETA_ALLOWED_ROUTES.has(route.name)) {
      return route;
    }
    // All other routes redirect to community page
    return { ...route, component: () => <NamedRedirect name="CommunityPage" /> };
  });
};

export default routeConfiguration;
